import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { Clock } from "lucide-react";

const STATUS_BADGE: Record<string, string> = {
  open:        "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  completed:   "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  closed:      "bg-gray-100 text-gray-500",
};

const ContractLaborWorkOrders = () => {
  const { user } = useAuth();
  const navigate = useTabNavigate();

  const { data: workOrders = [], isLoading } = useQuery({
    queryKey: ["cl_work_orders", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("id, number, status, description, scope_notes, created_at, projects(name, city, state)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">My Work Orders</h1>
      <p className="text-sm text-muted-foreground">Work orders assigned to you. You cannot access financial details.</p>

      {workOrders.length === 0 ? (
        <p className="text-muted-foreground">No work orders assigned to you.</p>
      ) : (
        <div className="space-y-3">
          {workOrders.map((wo: any) => {
            const project = wo.projects;
            return (
              <div key={wo.id} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">{wo.number}</span>
                    <Badge variant="outline" className={STATUS_BADGE[wo.status] ?? "bg-gray-100 text-gray-500"}>
                      {wo.status?.replace("_", " ")}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => navigate(`/portal/contract-labor/log-hours?wo=${wo.id}`)}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    Log Hours
                  </Button>
                </div>
                {project && (
                  <p className="text-sm text-muted-foreground">
                    {project.name}
                    {(project.city || project.state) && (
                      <span> · {[project.city, project.state].filter(Boolean).join(", ")}</span>
                    )}
                  </p>
                )}
                {wo.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{wo.description}</p>
                )}
                {wo.scope_notes && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground">Scope Notes</summary>
                    <p className="mt-1 pl-2 border-l-2 border-muted text-muted-foreground">{wo.scope_notes}</p>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContractLaborWorkOrders;
